// src/features/videos/VideoDetailPage.tsx — the /videos/:id route. A sample id draws the
// SAMPLE watch page below (player placeholder + title + description + tags, an "up next"
// rail, and a comment thread); any other id is a real video post (real/RealWatchPage).
// The sample player REMAINS a placeholder (DisabledHint play glyph).
//
// Layout (2026-09-26, owner's concept video_watch_page.webp): Up Next goes where
// useWatchRail puts it — the shell's right column on desktop (it used to sit empty there
// while the page squeezed its own rail in beside a narrower player), beside the details in
// theater mode (which borrows that column for the player), under them below desktop.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { GeneratedThumb } from '@/components/GeneratedThumb/GeneratedThumb';
import { ThumbBadge } from '@/components/GeneratedThumb/ThumbBadge';
import { PlayGlyph } from '@/components/GeneratedThumb/PlayGlyph';
import { Avatar, DisabledHint, MetaRow } from '@/ui';
import { Icon } from '@/icons';
import { useVideo, useVideos, useViewport } from '@/hooks';
import type { VideoItem } from '@/core/models/video';
import { useVideoTheater } from './useVideoTheater';
import { useWatchRail } from './useWatchRail';
import { CommentsArea, DetailsArea, PlayerArea, RailArea, WatchGrid } from './watchGrid';
import { WatchRail } from './WatchRail';
import { VideoCommentsSection } from './comments/VideoCommentsSection';
import { formatDuration, formatViews } from './meta';
import { PreviewNotice } from '@/components/PreviewNotice';
import { RealWatchPage } from './real/RealWatchPage';

// ─── Fullscreen helpers (real Fullscreen API, with a cheap webkit fallback) ────

interface FullscreenCapableElement extends HTMLElement {
  webkitRequestFullscreen?: () => Promise<void> | void;
}

interface FullscreenCapableDocument extends Document {
  webkitExitFullscreen?: () => Promise<void> | void;
  webkitFullscreenElement?: Element | null;
}

// Swallow a possibly-void, possibly-Promise-returning fullscreen call so a
// rejection (permission denied, no user gesture, etc.) never surfaces as an
// unhandled rejection — there is nothing more to do than leave the UI as-is.
function swallow(result: Promise<void> | void): void {
  if (result && typeof result.catch === 'function') {
    result.catch(() => {
      /* nothing to do — fullscreen simply didn't happen */
    });
  }
}

function requestFullscreen(el: FullscreenCapableElement): void {
  if (el.requestFullscreen) {
    swallow(el.requestFullscreen());
  } else if (el.webkitRequestFullscreen) {
    swallow(el.webkitRequestFullscreen());
  }
  // Neither API present: no-op — the button stays visible but inert, same
  // spirit as the rest of this page's disabled-affordance handling.
}

function exitFullscreen(): void {
  const doc = document as FullscreenCapableDocument;
  if (document.exitFullscreen) {
    swallow(document.exitFullscreen());
  } else if (doc.webkitExitFullscreen) {
    swallow(doc.webkitExitFullscreen());
  }
}

function currentFullscreenElement(): Element | null {
  const doc = document as FullscreenCapableDocument;
  return document.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
}

// ─── Styled: player ──────────────────────────────────────────────────────────

// The sized, positioned player frame. DisabledHint wraps ONLY the PlayGlyph
// inside it (not this whole frame) — an inline-flex wrapper whose only content
// is absolutely positioned collapses to 0×0, which is exactly what happened when
// DisabledHint used to wrap this entire element. See ui/DisabledHint's sizing note.
const PlayerWrap = styled.div<{ $theater: boolean }>`
  position: relative;
  border-radius: ${({ theme }) => theme.radius.md};
  overflow: hidden;
  aspect-ratio: 16 / 9;

  /* Theater mode widens the player a lot. Cap its height so a wide viewport
     doesn't push the title/description/comments below the fold, and center
     the (now width-auto) frame in the column. */
  ${({ $theater }) =>
    $theater &&
    css`
      max-height: 80vh;
      width: auto;
      margin: 0 auto;
    `}

  /* Fullscreen only shows what's inside this element. A plain black fill
     keeps letterboxing from reading as a bug. */
  &:fullscreen,
  &:-webkit-full-screen {
    aspect-ratio: unset;
    max-height: none;
    width: 100%;
    margin: 0;
    border-radius: 0;
    background: #000;
  }
`;

const PlayOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
`;

const PlayerControls = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 2;
  display: flex;
  gap: 6px;
`;

const PlayerBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: none;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: rgba(0, 0, 0, 0.8);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

// ─── Styled: meta ────────────────────────────────────────────────────────────

const Title = styled.h1`
  margin: 18px 0 10px;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: 700;
  color: ${({ theme }) => theme.color.textPrimary};
`;

const AuthorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
`;

const AuthorText = styled.div`
  display: flex;
  flex-direction: column;
`;

const AuthorName = styled.span`
  font-family: ${({ theme }) => theme.font.body};
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const MetaLine = styled.span`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Description = styled.p`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  line-height: 1.6;
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
`;

const Tag = styled.span`
  padding: 3px 9px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: 11px;
  color: ${({ theme }) => theme.color.textMuted};
`;

const NotFoundWrap = styled.div`
  padding: 40px 16px;
  text-align: center;
  font-family: ${({ theme }) => theme.font.body};
  color: ${({ theme }) => theme.color.textMuted};
`;

export function VideoDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { video, loaded } = useVideo(id);

  // Not a sample id → a real video post (2026-09-26). RealWatchPage owns its own loading
  // and "couldn't be found" states. (Separate components on purpose: each publishes its
  // own rail, and a parent's effects run AFTER its child's — a shared parent would wipe
  // the real page's rail.)
  if (loaded && !video) {
    return id ? (
      <RealWatchPage postId={id} />
    ) : (
      <NotFoundWrap>
        <p>This video couldn&apos;t be found.</p>
        <Link to="/videos">Back to Videos</Link>
      </NotFoundWrap>
    );
  }

  if (!video) return null;
  return <SampleWatch video={video} />;
}

function SampleWatch({ video }: { video: VideoItem }) {
  const { videos, creators } = useVideos();
  const creator = creators.find((c) => c.id === video.authorId);

  const theater = useVideoTheater((s) => s.theater);
  const toggleTheater = useVideoTheater((s) => s.toggle);

  // Theater mode is desktop-only: below 1024px there is no right column to borrow, so
  // hide the control and ignore a stale `true` from localStorage (e.g. toggled on
  // desktop, then resized).
  const viewport = useViewport();
  const isDesktop = viewport === 'desktop';
  const effectiveTheater = theater && isDesktop;

  const playerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    function onChange() {
      setIsFullscreen(currentFullscreenElement() === playerRef.current);
    }
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
    };
  }, []);

  const handleFullscreen = useCallback(() => {
    if (isFullscreen) {
      exitFullscreen();
    } else if (playerRef.current) {
      requestFullscreen(playerRef.current);
    }
  }, [isFullscreen]);

  const rail = useMemo(
    () => <WatchRail current={video} videos={videos} creators={creators} />,
    [video, videos, creators],
  );
  const railPlace = useWatchRail(rail, effectiveTheater);

  return (
    <>
      <PreviewNotice text="Sample video: this shows how the watch page will look. Nothing here plays yet." />
      <WatchGrid $rail={railPlace} data-testid="watch-grid">
        <PlayerArea>
          <PlayerWrap ref={playerRef} $theater={effectiveTheater} data-testid="video-player">
            <GeneratedThumb seedKey={video.id} fill />
            <PlayOverlay>
              <DisabledHint explanation="Playback isn't available yet. This is a placeholder.">
                {(describedBy) => <PlayGlyph describedBy={describedBy} size={64} tone="surface" />}
              </DisabledHint>
            </PlayOverlay>
            <ThumbBadge>{formatDuration(video.durationSec)}</ThumbBadge>
            <PlayerControls>
              {isDesktop && (
                <PlayerBtn
                  type="button"
                  aria-label="Theater mode"
                  aria-pressed={theater}
                  onClick={toggleTheater}
                >
                  <Icon name="theater" size={18} />
                </PlayerBtn>
              )}
              <PlayerBtn
                type="button"
                aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                aria-pressed={isFullscreen}
                onClick={handleFullscreen}
              >
                <Icon name={isFullscreen ? 'minimize' : 'maximize'} size={18} />
              </PlayerBtn>
            </PlayerControls>
          </PlayerWrap>
        </PlayerArea>

        <DetailsArea>
          <Title>{video.title}</Title>

          <AuthorRow>
            {creator && <Avatar name={creator.name} avatarKey={creator.av} size={44} />}
            <AuthorText>
              {creator && <AuthorName>{creator.name}</AuthorName>}
              <MetaLine>
                <MetaRow>
                  <span>{formatViews(video.views)}</span>
                  <span>{video.ageLabel}</span>
                </MetaRow>
              </MetaLine>
            </AuthorText>
          </AuthorRow>

          <Description>{video.description}</Description>

          <Tags>
            {video.tags.map((tag) => (
              <Tag key={tag}>{tag}</Tag>
            ))}
          </Tags>
        </DetailsArea>

        {railPlace !== 'shell' && <RailArea>{rail}</RailArea>}

        <CommentsArea>
          <VideoCommentsSection videoId={video.id} />
        </CommentsArea>
      </WatchGrid>
    </>
  );
}
